import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import { API } from 'API'
import { AppRoutes } from 'constants/appRoutes'
import { getUserProfile } from 'core/auth'
import {
  AccessRoleCategory,
  resolveAccessRoleCategory,
  UserRoles,
  UserOperatorRoles,
} from 'domains/user'

const navLinks = [
  { route: AppRoutes.cases, title: 'Cases', desc: 'View and manage patient cases' },
  { route: AppRoutes.users, title: 'Users', desc: 'Search and manage user accounts' },
  { route: AppRoutes.accounts, title: 'Accounts', desc: 'Account details and settings' },
  { route: AppRoutes.scanners, title: 'Scanners', desc: 'Scanner device management' },
  { route: AppRoutes.supports, title: 'Supports', desc: 'Support request management' },
  { route: AppRoutes.itero, title: 'iTero', desc: 'iTero scanner integrations' },
  { route: AppRoutes.webcontent, title: 'WebContent', desc: 'Manage web content entries' },
  { route: AppRoutes.pvs, title: 'PVS', desc: 'PVS data and configurations' },
  { route: AppRoutes.transfer, title: 'Transfer Cases', desc: 'Transfer cases between accounts' },
  { route: AppRoutes.addresses, title: 'Manage Addresses', desc: 'Address records management' },
]

const responsibleUsersLink = {
  route: AppRoutes.responsibleUsers,
  title: 'My Responsibility Scope',
  desc: 'View users within your responsibility scope',
}

const findAdminsLink = {
  route: AppRoutes.findAdmins,
  title: 'Find the Admins',
  desc: 'Find Admin by Country and Department',
}

export const NavPage = () => {
  const navigate = useNavigate()
  const user = getUserProfile()
  const [accessRoleCategory, setAccessRoleCategory] = useState<AccessRoleCategory>(
    AccessRoleCategory.NON_ADMIN,
  )

  const isResponsibleUsersAllowed =
    accessRoleCategory === AccessRoleCategory.ADMIN ||
    accessRoleCategory === AccessRoleCategory.SUPER_ADMIN

  useEffect(() => {
    if (user?.authorities.includes(UserOperatorRoles.ROLE_SCANNER) === true) {
      document.title = 'Support Scan Toolset'
      navigate(AppRoutes.scans)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const username = user?.user_name

    if (username == null || username === '') {
      return
    }

    API.getUser(username, true)
      .then((data) => {
        setAccessRoleCategory(resolveAccessRoleCategory(data.user.accessRoles))
      })
      .catch(() => {
        setAccessRoleCategory(AccessRoleCategory.NON_ADMIN)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {user?.authorities.includes(UserRoles.ROLE_USER_ADMIN) === true && (
        <div className="dashboard">
          <div className="dashboard__hero">
            <h1>Support Admin Toolset</h1>
            <p>Manage cases, users, accounts, and system configurations</p>
          </div>
          <div className="dashboard__grid">
            {navLinks.map(({ title, route, desc }, index) => (
              <Link className="dashboard__card" to={route} key={`${index}-${title}`}>
                <span className="dashboard__card-title">{title}</span>
                <p className="dashboard__card-desc">{desc}</p>
              </Link>
            ))}
            {isResponsibleUsersAllowed && (
              <Link className="dashboard__card" to={responsibleUsersLink.route}>
                <span className="dashboard__card-title">{responsibleUsersLink.title}</span>
                <p className="dashboard__card-desc">{responsibleUsersLink.desc}</p>
              </Link>
            )}
            {isResponsibleUsersAllowed && (
              <Link className="dashboard__card" to={findAdminsLink.route}>
                <span className="dashboard__card-title">{findAdminsLink.title}</span>
                <p className="dashboard__card-desc">{findAdminsLink.desc}</p>
              </Link>
            )}
          </div>
        </div>
      )}
      {user?.authorities.includes(UserOperatorRoles.ROLE_SCANNER) === true && (
        <div className="dashboard">
          <div className="dashboard__hero">
            <h1>Scan Operator Toolset</h1>
            <p>Manage scan operations and devices</p>
          </div>
          <div className="dashboard__grid">
            <Link className="dashboard__card" to={AppRoutes.scanners}>
              <span className="dashboard__card-title">Scans</span>
              <p className="dashboard__card-desc">View and manage scan operations</p>
            </Link>
          </div>
        </div>
      )}
    </>
  )
}
